import { useState, useEffect, useCallback } from 'react';
import { isMobileViewport, isTouchDevice, getDeviceOrientation, ORIENTATIONS } from '../utils/mobileResponsivenessValidation';

/**
 * Custom hook for mobile responsiveness features
 * 
 * Provides reactive mobile detection, orientation tracking, and responsive utilities
 */
export const useMobileResponsive = () => {
  const [state, setState] = useState({
    isMobile: false,
    isTouch: false,
    orientation: ORIENTATIONS?.PORTRAIT,
    viewport: {
      width: 0,
      height: 0
    },
    breakpoint: null
  });

  /**
   * Determines current breakpoint based on viewport width
   */
  const getCurrentBreakpoint = useCallback((width) => {
    if (width <= 320) return 'SMALL_MOBILE';
    if (width <= 375) return 'MOBILE';
    if (width <= 414) return 'LARGE_MOBILE';
    if (width <= 768) return 'TABLET';
    if (width <= 1024) return 'TABLET_LANDSCAPE';
    return 'DESKTOP';
  }, []);

  /**
   * Updates responsive state
   */
  const updateResponsiveState = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setState({
      isMobile: isMobileViewport(),
      isTouch: isTouchDevice(),
      orientation: getDeviceOrientation(),
      viewport: { width, height },
      breakpoint: getCurrentBreakpoint(width)
    });
  }, [getCurrentBreakpoint]);

  /**
   * Handles orientation change
   */
  const handleOrientationChange = useCallback(() => {
    // Delay to allow viewport to update
    setTimeout(updateResponsiveState, 100);
  }, [updateResponsiveState]);

  useEffect(() => {
    // Initial state
    updateResponsiveState();

    // Add event listeners
    window.addEventListener('resize', updateResponsiveState);
    window.addEventListener('orientationchange', handleOrientationChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateResponsiveState);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [updateResponsiveState, handleOrientationChange]);

  /**
   * Checks if current breakpoint matches specified breakpoint
   */
  const isBreakpoint = useCallback((breakpoint) => {
    return state?.breakpoint === breakpoint;
  }, [state?.breakpoint]);

  /**
   * Checks if viewport is at or below specified breakpoint
   */
  const isBreakpointOrBelow = useCallback((breakpoint) => {
    const breakpointOrder = ['SMALL_MOBILE', 'MOBILE', 'LARGE_MOBILE', 'TABLET', 'TABLET_LANDSCAPE', 'DESKTOP'];
    const currentIndex = breakpointOrder?.indexOf(state?.breakpoint);
    const targetIndex = breakpointOrder?.indexOf(breakpoint);
    return currentIndex <= targetIndex;
  }, [state?.breakpoint]);

  /**
   * Gets safe area insets for devices with notches
   */
  const getSafeAreaInsets = useCallback(() => {
    if (typeof window === 'undefined') return { top: 0, right: 0, bottom: 0, left: 0 };
    
    const getInset = (property) => {
      const value = getComputedStyle(document.documentElement)?.getPropertyValue(property);
      return parseInt(value) || 0;
    };

    return {
      top: getInset('--sat') || 0,
      right: getInset('--sar') || 0,
      bottom: getInset('--sab') || 0,
      left: getInset('--sal') || 0
    };
  }, []);

  return {
    ...state,
    isBreakpoint,
    isBreakpointOrBelow,
    getSafeAreaInsets,
    isPortrait: state?.orientation === ORIENTATIONS?.PORTRAIT,
    isLandscape: state?.orientation === ORIENTATIONS?.LANDSCAPE
  };
};

/**
 * Hook for managing mobile navigation state
 */
export const useMobileNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useMobileResponsive();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  // Close navigation when switching to desktop
  useEffect(() => {
    if (!isMobile && isOpen) {
      close();
    }
  }, [isMobile, isOpen, close]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isOpen]);

  return {
    isOpen,
    open,
    close,
    toggle,
    isMobile
  };
};

/**
 * Hook for managing swipe gestures
 */
export const useSwipeGesture = (callbacks = {}) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart(e?.targetTouches?.[0]?.clientX);
  }, []);

  const onTouchMove = useCallback((e) => {
    setTouchEnd(e?.targetTouches?.[0]?.clientX);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && callbacks?.onSwipeLeft) {
      callbacks?.onSwipeLeft();
    }
    if (isRightSwipe && callbacks?.onSwipeRight) {
      callbacks?.onSwipeRight();
    }
  }, [touchStart, touchEnd, callbacks]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
};

export default useMobileResponsive;